import React, { useState } from 'react';
import { Table, Tooltip, Button, InputNumber, Modal, Avatar, Card, Icon, Progress } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from '../../../../../shared/components/NumberFormat';
import Colors from '../../../../../../themes/Colors';
import CustomInputCurrency from '../../../../../shared/components/CustomInputCurrency';
import { contentDisplayIsSuspended, isTotalDiscount } from '../../../../../../services/display';
import { SeparateNumberFormat, formatNumber } from '../../../../../../services/utils';
import { checkIsNewDisplayInEdit } from '../../../../../../services/campaignManager';
import NewScreenTag from '../../../../../shared/components/NewScreenTag';
import { impressions, transfer } from '../../../services/investmentCalculations';
import ImpressionsInput from '../supporting/ImpressionsInput';
import {
  isDynamicContent,
  contentPreviewSource,
  isVideoPreview,
  convertImage
} from '../../../../../../services/contents';
import DynamicContentLink from '../../../../../shared/components/DynamicContentLink';

const AdViewComponent = ({
  currency,
  contentsDisplaysSelect,
  updateImpressions,
  updateImpressionsToMax,
  getSpotStatusTag,
  messageStatus,
  calculateImpressionsView,
  getCurrentSubsidizedShows,
  // calculateConsumedImpressions,
  limitAddImpressions,
  calculateBudget,
  calculateDiffValue,
  updateBudget,
  getMaxBudgetForRecord,
  getMinSpotsForRecord,
  editCampaignActive,
  initialBackup,
  hasInitialBackup,
  getRecordFloorReasons
}) => {
  const { t } = useTranslation();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState({ file: '', type: '' });

  // Takes the whole content: a dynamic ad needs preview_file/preview_type too,
  // not just file and type.
  const handlePreview = (content) => {
    setPreviewContent(content);
    setPreviewVisible(true);
  };

  const handleCancelPreview = () => {
    setPreviewContent({ file: '', type: '' });
    setPreviewVisible(false);
  };

  const getMinimumInvestmentInfo = (record) => {
    const display = {
      minimum_programmatic_spots: record.minimum_programmatic_spots,
      minimum_programmatic_period: record.minimum_programmatic_period,
    };
    const minSpots = transfer.minimumInvestmentFloor(display, record.relationShowDays || 0);
    const minInvestment = minSpots * ((record.smart_campaign_cpm || 0) / 1000);
    return {
      minImpressions: SeparateNumberFormat(
        impressions.fromSpots(minSpots, record.multiplier)
      ),
      minInvestment: `$${currency} ${formatNumber(minInvestment)}`,
    };
  };

  const columns = [
    {
      title: t('Screen'),
      dataIndex: 'display_name',
      key: 'name',
      render: (text, record) => {
        const isNewDisplay =
          checkIsNewDisplayInEdit(record.displayId, initialBackup, hasInitialBackup);
        return (
          <div>
            {getSpotStatusTag(record, t)}
            {messageStatus(record)}
            <span>{text}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {isNewDisplay && <NewScreenTag style={{ marginRight: 0, marginTop: 5 }} />}
              {isNewDisplay && record.hasVolumeDiscounts && (
                <Tooltip
                  placement="top"
                  title={t(
                    'This screen has volume discounts configured, impressions will probably be added to it.'
                  )}
                >
                  <Icon
                    type="tags"
                    style={{
                      fontSize: '18px',
                      verticalAlign: 'middle',
                      color: '#52C41A'
                    }}
                  />
                </Tooltip>
              )}
            </span>
          </div>
        );
      }
    },
    {
      title: t('Version'),
      dataIndex: 'content_version_name',
      key: 'version',
      render: text => (
        <Tooltip title={text}>
          <span
            style={{
              display: 'inline-block',
              maxWidth: 120,
              padding: '0 8px',
              borderRadius: 10,
              background: Colors.primary,
              color: Colors.onPrimary,
              fontSize: 12,
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle'
            }}
          >
            {text}
          </span>
        </Tooltip>
      )
    },
    {
      title: t('Preview'),
      dataIndex: 'content.file_thumb',
      key: 'preview',
      render: (text, record) => {
        if (record.content.type && record.content.type.startsWith('video')) {
          return (
            <video
              width="64"
              height="64"
              autoPlay
              muted
              loop
              onClick={() => handlePreview(record.content)}
              style={{ cursor: 'pointer', objectFit: 'cover' }}
            >
              <source src={record.content.file} />
            </video>
          );
        }
        // Empty for a dynamic ad with no preview, and for one whose preview is
        // still transcoding ('pending') or failed ('error-transcode').
        const thumb = convertImage(record.content.file_thumb);
        if (!thumb && isDynamicContent(record.content.type)) {
          return (
            <div
              onClick={() => handlePreview(record.content)}
              style={{
                cursor: 'pointer',
                background: Colors.surfaceMuted,
                borderRadius: 4,
                width: 64,
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Icon type="global" style={{ fontSize: 32, color: Colors.primaryAccent }} />
            </div>
          );
        }
        return (
          <Avatar
            shape="square"
            size={64}
            src={record.content.file_thumb}
            onClick={() => handlePreview(record.content)}
            style={{ cursor: 'pointer' }}
          />
        );
      }
    },
    // {
    //   title: t('Consumed spots'),
    //   key: 'consumed_spots',
    //   render: (text, record) => {
    //     const consumed = calculateConsumedImpressions(record);
    //     const pending = Math.max(0,
    //       record.promised_shows_origin !== record.promised_shows_update
    //         ? record.promised_shows_update
    //         : calculateImpressionsView(record)
    //     );
    //     const total = consumed + pending;
    //     const percent = total > 0 ? Math.round((consumed / total) * 100) : 0;
    //     return (
    //       <Tooltip title={`${SeparateNumberFormat(consumed)} / ${SeparateNumberFormat(total)}`}>
    //         <Progress
    //           type="circle"
    //           width={40}
    //           percent={percent}
    //           strokeColor={consumed === 0 ? '#bfbfbf' : Colors.primary}
    //         />
    //       </Tooltip>
    //     );
    //   }
    // },
    {
      // title: (
      // <Tooltip placement="top" title={t('Transfer')}>
      // <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
      // <FontAwesomeIcon icon={faExchangeAlt} style={{color: Colors.primaryAccent}}/>
      // </div>
      // </Tooltip>
      // ),
      key: 'transfer',
      width: 50,
      render: (text, record) => {
        const isNewDisplay =
          checkIsNewDisplayInEdit(record.displayId, initialBackup, hasInitialBackup);
          
        return <Tooltip placement="top" title={t('All impressions are forwarded to this version')}>
          <Button
            shape="circle"
            disabled={contentDisplayIsSuspended(record)}
            className={"buttonActionsGroup " + (isNewDisplay ? "highlight" : "")}
            onClick={() => updateImpressionsToMax(record.relationId)}
            style={{ marginRight: '10px' }}
          >
            <FontAwesomeIcon icon={faExchangeAlt} style={{ color: isNewDisplay ? Colors.atention : Colors.primaryAccent }} />
          </Button>
        </Tooltip>
      }
    },
    {
      title: t('Pending impressions'),
      key: 'pending_impressions',
      render: (text, record) => {
        const maxSpots = Math.max(0, limitAddImpressions(record));
        const minSpots = getMinSpotsForRecord ? getMinSpotsForRecord(record) : 0;
        const value = record.promised_shows_origin !== record.promised_shows_update
                    ? record.promised_shows_update
                    : calculateImpressionsView(record)

        const maxValue = Math.max(0,value);
        return (
          <Tooltip title={t('Shows the remaining promised impressions')}>
            <ImpressionsInput
              promisedShows={maxValue}
              multiplier={record.multiplier}
              disabled={record.subsidized_price > 0}
              onChangePromisedShows={newPromisedShows => {
                const newValue = Math.min(Math.max(newPromisedShows, minSpots), maxSpots);
                updateImpressions(newValue, record.relationId);
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      title: t('Subtracted impressions'),
      key: 'remaining_impressions',
      render: (text, record) => (
        <InputNumber
          type="text"
          formatter={SeparateNumberFormat}
          parser={value => Number(value.replace(/\./g, ''))}
          disabled
          value={impressions.fromSpots(
            calculateDiffValue('subtract', record),
            record.multiplier
          )}
        />
      )
    },
    {
      title: t('Added impressions'),
      key: 'added_impressions',
      render: (text, record) => (
        <InputNumber
          type="text"
          formatter={SeparateNumberFormat}
          parser={value => Number(value.replace(/\./g, ''))}
          disabled
          value={impressions.fromSpots(calculateDiffValue('add', record), record.multiplier)}
        />
      )
    },
    {
      title: t('Budget'),
      key: 'budget',
      width: 250,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CustomInputCurrency
            currency={currency}
            value={calculateBudget('no_origin', record)}
            max={getMaxBudgetForRecord ? getMaxBudgetForRecord(record) : undefined}
            commitOnBlur
            onChange={value => updateBudget(value, record.relationId)}
            style={{ flex: 1 }}
            disabled={contentDisplayIsSuspended(record)}
          />
          {record.promised_shows_origin != record.promised_shows_update ? (
            <Tooltip
              placement="topLeft"
              title={`${t('Initial budget')}: ${calculateBudget('origin', record)}`}
            >
              <Icon
                style={{
                  fontSize: '15px',
                  marginLeft: '5px',
                  color: Colors.atention
                }}
                type="info-circle"
              />
            </Tooltip>
          ) : null}
        </div>
      )
    },
    {
      title: t('CPM/impression'),
      dataIndex: 'cpmi',
      key: 'cpmi',
      render: cpmi => (
        <Tooltip title={`${t('Cost per impression')}: $${(cpmi / 1000).toFixed(4)}`}>
          <span>
            <NumberFormat value={cpmi} currency={currency} />
          </span>
        </Tooltip>
      )
    },
    {
      title: t('Conditions'),
      key: 'restrictions',
      width: 180,
      render: (text, record) => {
        const reasons = getRecordFloorReasons
          ? getRecordFloorReasons(record)
          : {
              hasDiscount: !!record.appliedDiscount,
              hasMinimum: false,
              appliedDiscount: record.appliedDiscount || null,
            };
        if (!reasons.hasDiscount && !reasons.hasMinimum) {
          return <span style={{ color: Colors.gray }}>—</span>;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {reasons.hasDiscount && (
              <Tooltip
                title={
                  <div>
                    <Trans
                      i18nKey={
                        isTotalDiscount(record.appliedDiscount)
                          ? 'volume discount applied info total impressions'
                          : 'volume discount applied info impressions'
                      }
                      values={{
                        minImpressions: SeparateNumberFormat(
                          impressions.fromSpots(
                            (reasons.appliedDiscount && reasons.appliedDiscount.min_impressions) ||
                              0,
                            record.multiplier
                          )
                        )
                      }}
                      components={{ strong: <strong /> }}
                    />
                    <div style={{ marginTop: '6px' }}>
                      <Trans
                        i18nKey="volume discount extraction message impressions"
                        components={{ strong: <strong /> }}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: '8px',
                        borderTop: '1px solid rgba(255,255,255,0.2)',
                        paddingTop: '8px'
                      }}
                    >
                      {t('zero impressions warning')}
                    </div>
                  </div>
                }
              >
                <span
                  className="ant-tag ant-tag-orange"
                  style={{ fontSize: '11px', margin: 0, cursor: 'help' }}
                >
                  <Icon type="exclamation-circle" style={{ marginRight: '4px' }} />
                  {t('Volume discount')}
                </span>
              </Tooltip>
            )}
            {reasons.hasMinimum && (
              <Tooltip
                title={
                  <div>
                    <Trans
                      i18nKey="minimum investment extraction message impressions"
                      values={getMinimumInvestmentInfo(record)}
                      components={{ strong: <strong /> }}
                    />
                    <div
                      style={{
                        marginTop: '8px',
                        borderTop: '1px solid rgba(255,255,255,0.2)',
                        paddingTop: '8px'
                      }}
                    >
                      {t('zero impressions warning')}
                    </div>
                  </div>
                }
              >
                <span
                  className="ant-tag ant-tag-orange"
                  style={{ fontSize: '11px', margin: 0, cursor: 'help' }}
                >
                  <Icon type="exclamation-circle" style={{ marginRight: '4px' }} />
                  {t('Minimum investment')}
                </span>
              </Tooltip>
            )}
          </div>
        );
      }
    }
  ];
  return (
    <div>
      {contentsDisplaysSelect.map(collection => {
        return (
          <Card key={collection.key} title={collection.key} headStyle={{ fontWeight: 'bolder' }}>
            <Table
              columns={columns}
              dataSource={collection.customContents}
              rowKey="relationId"
              pagination={false}
            />
          </Card>
        );
      })}
      <Modal visible={previewVisible} footer={null} onCancel={handleCancelPreview}>
        {/* On a dynamic ad `file` is the ad URL, not media: what is shown is the
            uploaded preview (the globe when there is none or it is still
            transcoding), plus the link out to the page itself. */}
        {isDynamicContent(previewContent.type) ? (
          <div style={{ position: 'relative' }}>
            {contentPreviewSource(previewContent) ? (
              isVideoPreview(previewContent) ? (
                <video controls autoPlay muted loop width="100%">
                  <source src={contentPreviewSource(previewContent)} />
                </video>
              ) : (
                <img width="100%" src={contentPreviewSource(previewContent)} alt="" />
              )
            ) : (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <Icon type="global" style={{ fontSize: 32, color: Colors.primaryAccent }} />
              </div>
            )}
            {/* Floats over the preview instead of pushing it up; white plate so the
                link stays legible whatever the preview shows underneath. */}
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                borderRadius: 4,
                padding: '0 4px',
              }}
            >
              <DynamicContentLink url={previewContent.file} type="primary" />
            </div>
          </div>
        ) : previewContent.type && previewContent.type.startsWith('video') ? (
          <video controls autoPlay width="100%">
            <source src={previewContent.file} />
          </video>
        ) : (
          <img width="100%" src={previewContent.file} alt="" />
        )}
      </Modal>
    </div>
  );
};
export default AdViewComponent;
